import { ActionButton } from "components";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import SendLayerDebitNoteContainer from "./send-layer-debit-note";
import { Treaty_treaty } from "_graphql/queries/__generated__/Treaty";
import UpdateLayerLimitContainer from "./update-limit-layer";
import ViewLayerLimitDebitNoteContainer from "./view-debit-note";
import numeral from "numeral";
import { getLayerParticipantsPercentageTotal, withPermissions } from "utils";

type Props = {
  treaty: Treaty_treaty | null | undefined;
};

const LayerLimits = ({ treaty }: Props) => {
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();

  const orderHereOn = treaty?.order_hereon || 100;

  return (
    <>
      <div className="bg-card-500 rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-700/50">
          <h3 className="text-xl font-semibold text-primary">
            Treaty Layer Limits
          </h3>
          <p className="mt-1 text-sm text-tertiary">
            Commission rates for each surplus layer
          </p>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/50">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    Layer No.
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    Limit
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    Installments
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    Deductible
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    M&D Premium
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {JSON.parse(treaty?.layer_limit || "[]")
                  ?.map((_limit: any, index: number) => ({
                    ..._limit,
                    no: index + 1,
                    deductible: numeral(_limit.deductible).format("#,#.##"),
                    limit: numeral(_limit.limit).format("#,#.##"),
                    m_and_d_premium: numeral(_limit.m_and_d_premium).format(
                      "#,#.##"
                    ),
                  }))
                  ?.map((surplus: any, index: number) => (
                    <tr
                      key={surplus.id + index}
                      className="hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-primary">
                        {surplus.no}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary">
                        {surplus.limit}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary">
                        {surplus.installment_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary">
                        {surplus.deductible}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary">
                        {surplus.m_and_d_premium}
                      </td>
                      <td className="px-4 py-3 text-sm text-primary">
                        <>
                          {withPermissions([
                            "*:*",
                            "treaties:*",
                            "treaties:manage",
                            "treaties:update",
                          ])(
                            <ActionButton
                              action={"update"}
                              onClick={() =>
                                navigate({
                                  search(prev) {
                                    return {
                                      ...prev,
                                      layer: surplus?.no,
                                      modal: "update-layer",
                                    };
                                  },
                                })
                              }
                            />
                          )}
                          {getLayerParticipantsPercentageTotal(
                            treaty,
                            surplus?.no
                          ) >= orderHereOn && (
                            <>
                              {withPermissions([
                                "*:*",
                                "treaties:*",
                                "treaties:manage",
                                "treaties:send",
                              ])(
                                <ActionButton
                                  action={"send"}
                                  onClick={() =>
                                    navigate({
                                      search(prev) {
                                        return {
                                          ...prev,
                                          layer: surplus?.no,
                                          modal: "send-layer",
                                        };
                                      },
                                    })
                                  }
                                />
                              )}
                              <ActionButton
                                action={"view"}
                                onClick={() =>
                                  navigate({
                                    search(prev) {
                                      return {
                                        ...prev,
                                        layer: surplus?.no,
                                        modal: "view-layer",
                                      };
                                    },
                                  })
                                }
                              />
                            </>
                          )}
                        </>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {searchParams?.layer && (
        <>
          <ViewLayerLimitDebitNoteContainer
            open={searchParams?.modal === "view-layer"}
            setOpen={(val) =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    layer: val ? prev?.layer : undefined,
                    modal: val ? "view-layer" : undefined,
                  };
                },
              })
            }
          />
          <SendLayerDebitNoteContainer
            open={searchParams?.modal === "send-layer"}
            setOpen={(val) =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    layer: val ? prev?.layer : undefined,
                    modal: val ? "send-layer" : undefined,
                  };
                },
              })
            }
          />
          <UpdateLayerLimitContainer
            open={searchParams?.modal === "update-layer"}
            setOpen={(val) =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    layer: val ? prev?.layer : undefined,
                    modal: val ? "update-layer" : undefined,
                  };
                },
              })
            }
          />
        </>
      )}
    </>
  );
};

export default LayerLimits;
